import { DetailSectionTemplateWithTranslation } from "components/MainSectionTemplate/translation/DetailSectionTemplateWithTranslation";
import { RenderEditOptions, Section } from "components/MainSectionTemplate/types";
import { PreferredTemplatePill } from "components/Pill/PreferredTemplatePill";
import { BasicTemplatePill } from "components/Pill/BasicTemplatePill";
import OptionalTextAreaInput, { OptionalTextAreaDetails } from "components/ValidatedFields/OptionalTextAreaInput";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import { YesNoRadioGroupForInPageEditing, YesNoText } from "components/ValidatedFields/YesNoRadioGroup";
import React from "react";
import { useIsSystemOneAccount } from "common/SessionContext";

interface Props {
  title: string
  invoiceDocumentTemplate: server.dto.MessageTemplate
}

interface InvoiceTemplateGeneralState {
  title: string
  preferredTemplate: boolean
  basicTemplate: boolean
}

export const InvoiceTemplateDetailSection = (props: Props) => {
  const isSystemOneAccount = useIsSystemOneAccount();
  const [generalDetailsState, setGeneralDetailsState] = React.useState<InvoiceTemplateGeneralState>({
    title: props.invoiceDocumentTemplate.title,
    preferredTemplate: props.invoiceDocumentTemplate.preferredTemplate,
    basicTemplate: props.invoiceDocumentTemplate.basicTemplate,
  });

  const topSection: Section = {
    title: props.title,
    type: "regular",
    key: "generalSection",
    fields: [{
      key: "title",
      label: RESX.GeneralLabels.Name,
      render: () => <div>{generalDetailsState.title}</div>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <RequiredTextInput
          validatorKey="title"
          focusOnMount
          maxLength={serverReference.constants.messageTemplate.maxTitleLength}
          value={value}
          onChange={(name) => { onChange(name) }}
          onEnter={() => submit()}
          onEsc={() => cancel()}
        />,
        fullWidth: true,
        type: "confirmOrCancelButton",
        initialValue: generalDetailsState.title,
        submitChange: (title: string) => window.CoreApi.MessageTemplate.Update({
          expectAttachment: false,
          basicTemplate: generalDetailsState.basicTemplate,
          messageTemplateId: props.invoiceDocumentTemplate.messageTemplateId,
          title,
          preferredTemplate: generalDetailsState.preferredTemplate,
        }),
      },
    }, {
      key: "basicTemplate",
      label: RESX.Advancing.ReadOnly,
      hidden: !isSystemOneAccount && !generalDetailsState.basicTemplate,
      editDisabled: !isSystemOneAccount,
      render: () => <YesNoText value={generalDetailsState.basicTemplate} yesLabel={<BasicTemplatePill />} />,
      inPageEdit: {
        render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
          value={value}
          onChange={(v) => onChange(v, true)}
        />,
        fullWidth: true,
        type: "custom",
        initialValue: generalDetailsState.basicTemplate,
        submitChange: (basicTemplate: boolean) => window.CoreApi.MessageTemplate.Update({
          expectAttachment: false,
          basicTemplate,
          messageTemplateId: props.invoiceDocumentTemplate.messageTemplateId,
          title: generalDetailsState.title,
          preferredTemplate: generalDetailsState.preferredTemplate,
        }),
      }
    }, {
      key: "preferredTemplate",
      label: RESX.GeneralLabels.resxDefault,
      editDisabled: generalDetailsState.preferredTemplate,
      hidden: generalDetailsState.basicTemplate,
      render: () => <div>{generalDetailsState.preferredTemplate
        ? <PreferredTemplatePill />
        : <div>{RESX.GeneralLabels.No}</div>
      }</div>,
      inPageEdit: {
        render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
          value={value}
          onChange={(v) => onChange(v, true)}
        />,
        fullWidth: true,
        type: "custom",
        initialValue: generalDetailsState.preferredTemplate,
        submitChange: (preferredTemplate: boolean) => window.CoreApi.MessageTemplate.Update({
          expectAttachment: false,
          basicTemplate: generalDetailsState.basicTemplate,
          messageTemplateId: props.invoiceDocumentTemplate.messageTemplateId,
          title: generalDetailsState.title,
          preferredTemplate,
        }),
      }
    }],
  }

  const onValueChanged = (key: string, value: any) => {
    setGeneralDetailsState(prev => ({
      ...prev,
      [key]: value
    }));
  }

  const isInPageEditable = (props.invoiceDocumentTemplate.permissions.canModify && !generalDetailsState.basicTemplate) || isSystemOneAccount;

  return <DetailSectionTemplateWithTranslation
    queryKey={`invoiceTemplate-${props.invoiceDocumentTemplate.messageTemplateId}`}
    isInPageEditable={isInPageEditable}
    topSection={topSection}
    listApi={() => window.CoreApi.MessageTemplate.ListInvoiceDocumentTranslation(props.invoiceDocumentTemplate.messageTemplateId)}
    addTranslationApi={(language) => window.CoreApi.MessageTemplate.CreateInvoiceDocumentTranslation({
      messageTemplateId: props.invoiceDocumentTemplate.messageTemplateId,
      language,
    })}
    removeTranslationApi={(language) => window.CoreApi.MessageTemplate.DeleteTranslation({
      messageTemplateId: props.invoiceDocumentTemplate.messageTemplateId,
      language,
    })}
    renderFieldsForTranslation={(language, formStateForTranslation) => [{
      key: "debitInvoice",
      hideLabel: true,
      label: RESX.InvoiceDocument.DebitInvoice,
      render: () => <h3>{RESX.InvoiceDocument.DebitInvoice}</h3>
    }, {
      key: "debitNotes",
      label: RESX.Note.Notes,
      isEmpty: !formStateForTranslation.debitNotes,
      render: () => <OptionalTextAreaDetails>{formStateForTranslation.debitNotes}</OptionalTextAreaDetails>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextAreaInput
          focusOnMount
          maxLength={serverReference.constants.messageTemplate.maxTranslationDebitCreditNotesLength}
          value={value}
          onChange={(title) => { onChange(title); }}
          onCtrlEnter={() => submit()}
          onEsc={() => cancel()}
        />,
        fullWidth: true,
        type: "confirmOrCancelButton",
        initialValue: formStateForTranslation.debitNotes,
        submitChange: (debitNotes: string) => window.CoreApi.MessageTemplate.SetInvoiceDocumentTranslation({
          messageTemplateId: props.invoiceDocumentTemplate.messageTemplateId,
          creditNotes: formStateForTranslation.creditNotes,
          debitNotes,
          footer: formStateForTranslation.footer,
          language,
        }),
      },
    }, {
      key: "creditInvoice",
      hideLabel: true,
      label: RESX.InvoiceDocument.CreditInvoice,
      render: () => <h3>{RESX.InvoiceDocument.CreditInvoice}</h3>
    }, {
      key: "creditNotes",
      label: RESX.Note.Notes,
      isEmpty: !formStateForTranslation.creditNotes,
      render: () => <OptionalTextAreaDetails>{formStateForTranslation.creditNotes}</OptionalTextAreaDetails>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextAreaInput
          focusOnMount
          maxLength={serverReference.constants.messageTemplate.maxTranslationDebitCreditNotesLength}
          value={value}
          onChange={(title) => { onChange(title); }}
          onCtrlEnter={() => submit()}
          onEsc={() => cancel()}
        />,
        fullWidth: true,
        type: "confirmOrCancelButton",
        initialValue: formStateForTranslation.creditNotes,
        submitChange: (creditNotes: string) => window.CoreApi.MessageTemplate.SetInvoiceDocumentTranslation({
          messageTemplateId: props.invoiceDocumentTemplate.messageTemplateId,
          creditNotes,
          debitNotes: formStateForTranslation.debitNotes,
          footer: formStateForTranslation.footer,
          language,
        }),
      }
    }, {
      key: "other",
      hideLabel: true,
      label: "",
      render: () => <h3>{RESX.Company.OtherDetails}</h3>
    }, {
      key: "footer",
      label: "Footer",
      isEmpty: !formStateForTranslation.footer,
      render: () => <OptionalTextAreaDetails>{formStateForTranslation.footer}</OptionalTextAreaDetails>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextAreaInput
          focusOnMount
          maxLength={serverReference.constants.messageTemplate.maxTranslationFooterLength}
          value={value}
          onChange={(footer) => { onChange(footer); }}
          onCtrlEnter={() => submit()}
          onEsc={() => cancel()}
        />,
        fullWidth: true,
        type: "confirmOrCancelButton",
        initialValue: formStateForTranslation.footer,
        submitChange: (footer: string) => window.CoreApi.MessageTemplate.SetInvoiceDocumentTranslation({
          messageTemplateId: props.invoiceDocumentTemplate.messageTemplateId,
          creditNotes: formStateForTranslation.creditNotes,
          debitNotes: formStateForTranslation.debitNotes,
          footer,
          language,
        }),
      }
    }]}
    onValueChanged={onValueChanged}
  />
}
